import { CanvasTexture, LinearFilter, SRGBColorSpace } from "three";
import {
  DEVICE_TYPE_COLOR,
  STATUS_COLOR,
  SELECTED_COLOR,
  type DeviceType,
  type DeviceStatus,
} from "./device";

/**
 * 디바이스 마커를 캔버스(2D)에 그려 THREE 텍스처로 만든다.
 *
 * 왜 캔버스 텍스처인가:
 * - 라벨이 씬 안의 sprite로 렌더 → depthTest로 건물에 자연 가림(occlusion)이 됨
 *   (drei <Html>은 DOM이라 depth와 무관 → 별도 raycast 해킹이 필요했음)
 * - canvas 2D fillText는 앱이 이미 로드한 Pretendard로 한글이 그대로 그려짐
 *   (troika 등 in-canvas 텍스트는 한글 폰트 파일을 별도로 로드해야 함)
 */

// SVG path d (각 type 아이콘, 20x20 viewBox)
const TYPE_ICON_PATH: Record<DeviceType, string> = {
  cctv: "M3 7h10v8H3zM13 9l5-2v10l-5-2z",
  thermo:
    "M10 3a2 2 0 0 1 2 2v8.5a3.5 3.5 0 1 1-4 0V5a2 2 0 0 1 2-2zm0 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  gas: "M8 2h4v3h-1v2h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2V5H8V2z",
  band: "M7 4h6l1 4h-8zM7 16l-1 4h8l-1-4zM6 8h8v8H6z",
  broadcast: "M3 9h3l5-3v12l-5-3H3zM14 7c1.5 1 1.5 5 0 6M16 5c2.5 2 2.5 8 0 10",
};

const dpr = () => Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);

function makeCanvas(w: number, h: number) {
  const d = dpr();
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(w * d);
  canvas.height = Math.ceil(h * d);
  const ctx = canvas.getContext("2d")!;
  ctx.scale(d, d);
  return { canvas, ctx };
}

function toTexture(canvas: HTMLCanvasElement): CanvasTexture {
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.minFilter = LinearFilter; // NPOT canvas — mipmap 비활성
  tex.generateMipmaps = false;
  tex.anisotropy = 4;
  return tex;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** 원형 아이콘 마커 텍스처 (정사각). type 색 + status 링 + 흰색 아이콘. */
export function createIconTexture(
  type: DeviceType,
  level: DeviceStatus["level"],
  selected: boolean
): CanvasTexture {
  const S = 64;
  const { canvas, ctx } = makeCanvas(S, S);
  const c = S / 2;
  const r = S / 2 - 6;

  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fillStyle = DEVICE_TYPE_COLOR[type];
  ctx.fill();
  ctx.lineWidth = selected ? 5 : 3;
  ctx.strokeStyle = selected ? SELECTED_COLOR : STATUS_COLOR[level];
  ctx.stroke();

  // 아이콘 (20x20 viewBox) 중앙 배치
  const iconSize = r * 1.15;
  const scale = iconSize / 20;
  ctx.save();
  ctx.translate(c - iconSize / 2, c - iconSize / 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = "#ffffff";
  ctx.fill(new Path2D(TYPE_ICON_PATH[type]));
  ctx.restore();

  return toTexture(canvas);
}

/** 텍스트 라벨 텍스처 + 가로세로비. Pretendard로 한글 렌더. */
export function createLabelTexture(text: string): { texture: CanvasTexture; aspect: number } {
  const FS = 26;
  const PAD_X = 12;
  const PAD_Y = 7;
  const H = FS + PAD_Y * 2;
  const font = `600 ${FS}px Pretendard, ui-monospace, monospace`;

  // 1차 캔버스로 폭 측정
  const m = document.createElement("canvas").getContext("2d")!;
  m.font = font;
  const tw = m.measureText(text).width;
  const W = Math.ceil(tw + PAD_X * 2);

  const { canvas, ctx } = makeCanvas(W, H);
  roundRect(ctx, 0, 0, W, H, 8);
  ctx.fillStyle = "rgba(0,0,0,0.82)";
  ctx.fill();

  ctx.font = font;
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(text, PAD_X, H / 2 + 1);

  return { texture: toTexture(canvas), aspect: W / H };
}
